import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box, Typography, Button, IconButton, Tooltip, Chip, CircularProgress,
  Dialog, Grid, Select, MenuItem, TextField, Breadcrumbs, Link,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  Accordion, AccordionSummary, AccordionDetails, Divider,
  Radio, RadioGroup, FormControlLabel, Popover,
  Menu, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Science as ScienceIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  Biotech as BiotechIcon,
  SwapVert as SwapVertIcon,
  Print as PrintIcon,
  EggAlt as EggAltIcon,
  Article as ArticleIcon,
  MedicalInformation as MedicalInformationIcon,
  Bloodtype as BloodtypeIcon,
  Medication as MedicationIcon,
  Bolt as BoltIcon,
  Assignment as AssignmentIcon,
  Comment as CommentIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import FolliculometryPrint from './FolliculometryPrint';
import FolliculometryComparisonSheet from './FolliculometryComparisonSheet';
import EmbroTransferModal from './EmbroTransferModal';
import EmbryoFetMonitoringModal from './EmbryoFetMonitoringModal';
import EmbryoOutcomeModal from './EmbryoOutcomeModal';
import FinalOutcomeModal from './FinalOutcomeModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Swal from 'sweetalert2';
import MDBox from 'components/MDBox';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import LoaderOverlay from 'components/LoaderOverlay';
import { api } from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import MDDatePicker from 'components/MDDatePicker';
import MDTimePicker from 'components/MDTimePicker';

// ── Constants ────────────────────────────────────────────────────────────────

const FOLLICLE_SIZES = [
  '<10', '10', '10.5', '11', '11.5', '12', '12.5', '13', '13.5', '14',
  '14.5', '15', '15.5', '16', '16.5', '17', '17.5', '18', '18.5', '19',
  '19.5', '20', '20.5', '21', '>21', 'Cyst',
];

const CYCLE_TYPES = ['Agonist', 'Antagonist'];

const FOLLICULOMETRY_STATUSES = ['ONGOING', 'COMPLETED', 'CANCELLED'];

const BLADDER_REMARKS = ['Filled', 'Emptied', 'Neither'];

const DRUG_ROUTE_OPTIONS = {
  Injection: ['S/C', 'IM'],
  Tablets: ['P/O', 'PV'],
};

const emptyDrug = () => ({
  drug_type: '',
  drug_name: '',
  irm_ivf_drug_master_id: null,
  dose: '',
  route: '',
  days: '',
  times: [],
});

const emptyOocyte = () => ({
  ultrasound: '',
  vaginal: '',
  others: '',
  ga: '',
  date: '',
  start_of_operation: '',
  duration: '',
  surgeon: '',
  anaesthetist: '',
  embryologist: '',
  leading_follicle_size_right: '',
  leading_follicle_size_left: '',
  follicles_punctured_right: '',
  follicles_punctured_left: '',
  eggs_retrieved_right: '',
  eggs_retrieved_left: '',
  ovarian_stabs_right: '',
  ovarian_stabs_left: '',
  abdominal_stabs_right: '',
  abdominal_stabs_left: '',
  bladder_remarks: '',
  comments: '',
  complications: '',
  embryos_frozen_stage: '',
});

const STATUS_STYLE = {
  ONGOING:   { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
  COMPLETED: { bg: '#ede7f6', color: '#4527a0', border: '#ce93d8' },
  CANCELLED: { bg: '#ffebee', color: '#b71c1c', border: '#ef9a9a' },
};

// ── Empty day form ────────────────────────────────────────────────────────────
const emptyDay = () => ({
  date: '',
  cycle_day: '',
  endometrium: '',
  cycle_type: '',
  agonist_value: '',
  antagonist_value: '',
  actions: '',
  comments: '',
  records: FOLLICLE_SIZES.flatMap(size => [
    { side: 'right', follicle_size: size, count: 0 },
    { side: 'left',  follicle_size: size, count: 0 },
  ]),
  params: [],
  drugs: [],
});

// ── Helpers ───────────────────────────────────────────────────────────────────
function recordsToGrid(records) {
  // { [size]: { right: count, left: count } }
  const grid = {};
  FOLLICLE_SIZES.forEach(s => { grid[s] = { right: 0, left: 0 }; });
  (records || []).forEach(r => {
    if (grid[r.follicle_size]) grid[r.follicle_size][r.side] = r.count;
  });
  return grid;
}

function gridToRecords(grid) {
  return FOLLICLE_SIZES.flatMap(size => [
    { side: 'right', follicle_size: size, count: Number(grid[size]?.right) || 0 },
    { side: 'left',  follicle_size: size, count: Number(grid[size]?.left)  || 0 },
  ]);
}

// ── Param row component ───────────────────────────────────────────────────────
function ParamRow({ param, options, onChange, onRemove }) {
  return (
    <Box display="flex" gap={1} alignItems="center" mb={0.75}>
      <Select
        size="small"
        value={param.param_name}
        onChange={e => {
          const opt = options.find(o => o.label === e.target.value);
          onChange('param_name', e.target.value, opt?.id || null);
        }}
        displayEmpty
        sx={{ minWidth: 200, fontSize: '0.8rem', height: 36 }}
        MenuProps={{ PaperProps: { style: { maxHeight: 260 } } }}
      >
        <MenuItem value="" disabled sx={{ fontSize: '0.8rem' }}><em>Select…</em></MenuItem>
        {options.map(o => (
          <MenuItem key={o.id} value={o.label} sx={{ fontSize: '0.8rem' }}>{o.label}</MenuItem>
        ))}
      </Select>
      <TextField
        size="small"
        placeholder="Value (optional)"
        value={param.param_value || ''}
        onChange={e => onChange('param_value', e.target.value)}
        sx={{ flex: 1, '& input': { fontSize: '0.8rem' }, '& .MuiInputBase-root': { height: 36 } }}
      />
      <IconButton size="small" onClick={onRemove} sx={{ color: '#dc3545' }}>
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

ParamRow.propTypes = {
  param: PropTypes.shape({
    param_type: PropTypes.string,
    param_name: PropTypes.string,
    param_value: PropTypes.string,
  }).isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, label: PropTypes.string })).isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

// ── Drug row component (structured Drugs advice, rendered as a table row) ──────
const drugSelectSx = {
  fontSize: '0.8rem',
  '& .MuiSelect-select': { py: '7.5px', minHeight: '40px', boxSizing: 'border-box', display: 'flex', alignItems: 'center' },
};
const drugInputSx = { '& .MuiInputBase-root': { minHeight: '40px' }, '& .MuiInputBase-input': { fontSize: '0.8rem', py: '7.5px', boxSizing: 'border-box' } };

// ── Multi-time picker cell: add times one at a time via a popup, shown as removable chips ──
function DrugTimesCell({ times, onChange }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingTime, setPendingTime] = useState('');

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    setPendingTime('');
  };
  const handleClose = () => {
    setAnchorEl(null);
    setPendingTime('');
  };
  const handleAddTime = () => {
    if (!pendingTime || times.includes(pendingTime)) return;
    onChange([...times, pendingTime]);
    setPendingTime('');
  };
  const handleRemoveTime = (t) => {
    onChange(times.filter(x => x !== t));
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', minHeight: '40px' }}>
      {times.map(t => (
        <Chip key={t} label={t} size="small" onDelete={() => handleRemoveTime(t)}
          sx={{ height: 22, fontSize: '0.68rem', backgroundColor: '#f3e5f5', color: '#6a1b9a', '& .MuiChip-deleteIcon': { fontSize: 14, color: '#6a1b9a99' } }} />
      ))}
      <IconButton size="small" onClick={handleOpen}
        sx={{ color: '#6a1b9a', border: '1px dashed #6a1b9a60', borderRadius: '6px', width: 26, height: 26 }}>
        <AddIcon sx={{ fontSize: 15 }} />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 2000 }}
        PaperProps={{ sx: { zIndex: 2000, backgroundColor: '#fff', boxShadow: '0 6px 24px rgba(0,0,0,0.28)' } }}
      >
        <Box sx={{ p: 1.5, width: 230 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#6a1b9a', textTransform: 'uppercase', letterSpacing: '0.03em', mb: 1 }}>
            Add Time
          </Typography>
          <MDTimePicker value={pendingTime} onChange={val => setPendingTime(val || '')}
            textFieldProps={{ fullWidth: true, size: 'small' }} />
          <Box display="flex" justifyContent="flex-end" gap={1} mt={1.5}>
            <Button size="small" onClick={handleClose}
              sx={{ textTransform: 'none', color: '#6c757d' }}>Done</Button>
            <Button size="small" variant="contained" onClick={handleAddTime} disabled={!pendingTime}
              sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#6a1b9a !important', color: '#fff !important' }}>
              Add
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}

DrugTimesCell.propTypes = {
  times: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

function DrugRow({ drug, drugOptions, onChange, onRemove }) {
  const nameOptions = drugOptions.filter(d => d.drug_type === drug.drug_type);
  const routeOptions = DRUG_ROUTE_OPTIONS[drug.drug_type] || [];

  return (
    <TableRow sx={{ '&:hover': { backgroundColor: '#faf5fc' } }}>
      <TableCell sx={{ py: 0.75, px: 1, verticalAlign: 'middle' }}>
        <Select fullWidth size="small" displayEmpty value={drug.drug_type} sx={drugSelectSx}
          onChange={e => onChange('drug_type', e.target.value)}>
          <MenuItem value="" disabled sx={{ fontSize: '0.8rem' }}><em>Type…</em></MenuItem>
          <MenuItem value="Injection" sx={{ fontSize: '0.8rem' }}>Injection</MenuItem>
          <MenuItem value="Tablets" sx={{ fontSize: '0.8rem' }}>Tablets</MenuItem>
        </Select>
      </TableCell>
      <TableCell sx={{ py: 0.75, px: 1, verticalAlign: 'middle' }}>
        <Select fullWidth size="small" displayEmpty value={drug.drug_name} disabled={!drug.drug_type} sx={drugSelectSx}
          MenuProps={{ PaperProps: { style: { maxHeight: 260 } } }}
          onChange={e => {
            const opt = nameOptions.find(o => o.drug_name === e.target.value);
            onChange('drug_name', e.target.value, opt?.id || null);
          }}>
          <MenuItem value="" disabled sx={{ fontSize: '0.8rem' }}>
            <em>{drug.drug_type ? 'Select drug…' : 'Select type first'}</em>
          </MenuItem>
          {nameOptions.map(o => (
            <MenuItem key={o.id} value={o.drug_name} sx={{ fontSize: '0.8rem' }}>{o.drug_name}</MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell sx={{ py: 0.75, px: 1, verticalAlign: 'middle' }}>
        <TextField fullWidth size="small" placeholder="Dose" value={drug.dose} sx={drugInputSx}
          onChange={e => onChange('dose', e.target.value)} />
      </TableCell>
      <TableCell sx={{ py: 0.75, px: 1, verticalAlign: 'middle' }}>
        {routeOptions.length > 0 ? (
          <RadioGroup row value={drug.route} onChange={e => onChange('route', e.target.value)}
            sx={{ flexWrap: 'nowrap', alignItems: 'center', minHeight: '40px' }}>
            {routeOptions.map(r => (
              <FormControlLabel key={r} value={r} label={r}
                sx={{
                  display: 'inline-flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  mr: 1.5,
                  ml: 0,
                  whiteSpace: 'nowrap',
                  '& .MuiFormControlLabel-label': { fontSize: '0.78rem', whiteSpace: 'nowrap' },
                }}
                control={<Radio size="small" sx={{ p: 0.4 }} />} />
            ))}
          </RadioGroup>
        ) : (
          <Typography sx={{ fontSize: '0.75rem', color: '#bbb' }}>—</Typography>
        )}
      </TableCell>
      <TableCell sx={{ py: 0.75, px: 1, verticalAlign: 'middle' }}>
        <TextField fullWidth size="small" placeholder="Days" value={drug.days} sx={drugInputSx}
          onChange={e => onChange('days', e.target.value)} />
      </TableCell>
      <TableCell sx={{ py: 0.75, px: 1, verticalAlign: 'middle' }}>
        <DrugTimesCell times={drug.times || []} onChange={newTimes => onChange('times', newTimes)} />
      </TableCell>
      <TableCell align="center" sx={{ py: 0.75, px: 0.5, verticalAlign: 'middle' }}>
        <IconButton size="small" onClick={onRemove} sx={{ color: '#dc3545' }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

DrugRow.propTypes = {
  drug: PropTypes.shape({
    drug_type: PropTypes.string,
    drug_name: PropTypes.string,
    dose: PropTypes.string,
    route: PropTypes.string,
    days: PropTypes.string,
    times: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  drugOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, drug_type: PropTypes.string, drug_name: PropTypes.string })).isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

// ── Follicle grid editor ──────────────────────────────────────────────────────
function FollicleGrid({ grid, onChange }) {
  return (
    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e9ecef', maxHeight: 320, overflowY: 'auto', width: '100%' }}>
      <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
        <TableBody>
          <TableRow sx={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', width: '34%', borderBottom: '2px solid #dee2e6' }}>Size (mm)</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', width: '33%', backgroundColor: '#fce4ec', color: '#c62828', borderBottom: '2px solid #dee2e6' }}>Right</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem', width: '33%', backgroundColor: '#e3f2fd', color: '#1565c0', borderBottom: '2px solid #dee2e6' }}>Left</TableCell>
          </TableRow>
          {FOLLICLE_SIZES.map(size => (
            <TableRow key={size} sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
              <TableCell sx={{ fontSize: '0.8rem', py: 0.4, fontWeight: 600 }}>{size}</TableCell>
              <TableCell align="center" sx={{ py: 0.5 }}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0, style: { textAlign: 'center', fontSize: '0.85rem', padding: '6px 4px' } }}
                  value={grid[size]?.right || 0}
                  onChange={e => onChange(size, 'right', parseInt(e.target.value) || 0)}
                  sx={{ width: { xs: '100%', sm: '100%', md: '40%' }, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                />
              </TableCell>
              <TableCell align="center" sx={{ py: 0.5 }}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0, style: { textAlign: 'center', fontSize: '0.85rem', padding: '6px 4px' } }}
                  value={grid[size]?.left || 0}
                  onChange={e => onChange(size, 'left', parseInt(e.target.value) || 0)}
                  sx={{ width: { xs: '100%', sm: '100%', md: '40%' }, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

FollicleGrid.propTypes = {
  grid: PropTypes.objectOf(
    PropTypes.shape({
      right: PropTypes.number,
      left: PropTypes.number,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

// ── Main Component ─────────────────────────────────────────────────────────────
export default function IVFFolliculometry() {
  const { procedureId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const primaryColor = theme.palette.logo?.main || '#00798E';
  const textColor = theme.palette.text?.main || '#344767';
  const secondaryColor = theme.palette.secondary?.main || '#7b809a';

  const { hasPermission } = usePermissions();
  const canEdit   = hasPermission('v1-ivf-update-procedure');
  const canDelete = hasPermission('v1-ivf-delete-procedure');

  const procedure = location.state?.procedure || null;
  const patient   = location.state?.patient   || null;

  // ── State ──────────────────────────────────────────────────────────
  const [loading, setLoading]       = useState(false);
  const [folliculometries, setFolliculometries] = useState([]);

  // Procedure code for the breadcrumb/header — location.state only carries the
  // procedure object when navigated to from within the app (e.g. from Procedure
  // Initiation) and is lost on a page refresh, so fetch it directly by id
  // whenever it isn't already available, instead of falling back to the numeric id.
  const [fetchedProcedureCode, setFetchedProcedureCode] = useState(null);
  useEffect(() => {
    if (procedure?.external_code || !procedureId) return;
    api.ivf.getProcedureById(procedureId).then(res => {
      if (res?.data?.external_code) setFetchedProcedureCode(res.data.external_code);
    }).catch(() => {});
  }, [procedure?.external_code, procedureId]);
  const procedureCode = procedure?.external_code || fetchedProcedureCode || `#${procedureId}`;

  // Create folliculometry dialog
  const [createOpen, setCreateOpen]   = useState(false);
  const [creating, setCreating]       = useState(false);

  // Change status dialog
  const [statusDialog, setStatusDialog] = useState({ open: false, id: null, currentStatus: '' });
  const [savingStatus, setSavingStatus] = useState(false);

  // Day dialog
  const [dayDialog, setDayDialog] = useState({ open: false, folliculometryId: null, dayId: null });
  const [dayForm, setDayForm] = useState(emptyDay());
  const [dayGrid, setDayGrid] = useState(() => recordsToGrid([]));
  const [savingDay, setSavingDay] = useState(false);

  // Expanded folliculometry id
  const [expandedId, setExpandedId] = useState(null);
  const [daysMap, setDaysMap] = useState({}); // { [folliculometryId]: days[] }
  const [loadingDays, setLoadingDays] = useState({});

  // "More actions" (⋮) dropdown for a folliculometry row
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, folliculometryId: null });

  // Param group (SERM / DRUGS / TRIGGER) from DB
  const [paramMembers, setParamMembers] = useState({}); // { SERM: [{id, name, options:[]}], ... }

  // Structured Drugs master (Injection / Tablets), for the Drugs sub-section
  const [drugMaster, setDrugMaster] = useState([]); // [{id, drug_type, drug_name}]

  // Doctors list for surgeon and embryologist dropdowns
  const [doctors, setDoctors] = useState([]);

  // Print preview
  const [printDialog, setPrintDialog] = useState({ open: false, folliculometry: null, days: [] });

  // Comparison Sheet Report (CS Report)
  const [comparisonSheet, setComparisonSheet] = useState({ open: false, folliculometry: null, days: [], fullScreen: false });

  // Oocyte Collection
  const [oocyteDialog, setOocyteDialog] = useState({ open: false, folliculometryId: null });
  const [oocyteCollections, setOocyteCollections] = useState({}); // { [folliculometryId]: [] }
  const [loadingOocyte, setLoadingOocyte] = useState({});
  const [oocyteForm, setOocyteForm] = useState(emptyOocyte());
  const [oocyteEditId, setOocyteEditId] = useState(null);
  const [savingOocyte, setSavingOocyte] = useState(false);

  // Embryo Transfer Modal
  const [embroTransferModalOpen, setEmbroTransferModalOpen] = useState({ open: false, folliculometryId: null });

  // FET Monitoring Modal
  const [fetMonitoringModalOpen, setFetMonitoringModalOpen] = useState({ open: false, procedureId: null });

  // Embryo Outcome Modal
  const [embryoOutcomeModalOpen, setEmbryoOutcomeModalOpen] = useState({ open: false, procedureId: null });
  const [finalOutcomeModalOpen, setFinalOutcomeModalOpen] = useState({ open: false, procedureId: null });

  // ── Load ───────────────────────────────────────────────────────────
  const loadFolliculometries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.ivf.getFolliculometries(procedureId);
      if (res?.flag === 1 || res?.statusCode === 200) {
        console.log('[IVF] Loaded folliculometries:', res.data);
        setFolliculometries(res.data || []);
      }
    } catch (e) {
      console.error('[IVF] Error loading folliculometries:', e);
    } finally {
      setLoading(false);
    }
  }, [procedureId]);

  useEffect(() => { loadFolliculometries(); }, [loadFolliculometries]);

  // Load folliculometry param group once on mount
  useEffect(() => {
    api.ivf.getFolliculometryParamGroup().then(res => {
      const members = res?.data?.param_group_members || [];
      const map = {};
      members.forEach(m => {
        // Transform: add 'options' property from 'param_group_member_options'
        map[m.name] = {
          ...m,
          options: m.param_group_member_options || m.options || [],
        };
      });
      setParamMembers(map);
    }).catch(() => {});
  }, []);

  // Load structured drug master (Injection / Tablets) once on mount
  useEffect(() => {
    api.ivf.getFolliculometryDrugMaster().then(res => {
      setDrugMaster(res?.data || []);
    }).catch(() => {});
  }, []);

  // Load doctors/consultants for surgeon and embryologist dropdowns
  useEffect(() => {
    const loadDoctors = async () => {
      try {
        const response = await api.contacts.getDoctorsList();
        if (response?.data) {
          setDoctors(response.data);
        }
      } catch (error) {
        console.error('Error loading doctors list:', error);
      }
    };
    loadDoctors();
  }, []);

  const loadDays = useCallback(async (folliculometryId) => {
    setLoadingDays(prev => ({ ...prev, [folliculometryId]: true }));
    try {
      const res = await api.ivf.getFolliculometryDays(folliculometryId);
      if (res?.flag === 1 || res?.statusCode === 200) {
        const data = res.data || [];
        setDaysMap(prev => ({ ...prev, [folliculometryId]: data }));
        return data;
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDays(prev => ({ ...prev, [folliculometryId]: false }));
    }
    return null;
  }, []);

  const handleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!daysMap[id]) loadDays(id);
  };

  // ── Create folliculometry ──────────────────────────────────────────
  const handleCreate = async () => {
    if (!procedure?.irm_contacts_id && !patient?.id) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Patient info missing.' });
      return;
    }
    setCreating(true);
    try {
      const res = await api.ivf.createFolliculometry(procedureId, {
        irm_contacts_id: procedure?.irm_contacts_id || patient?.id,
        irm_visits_id: procedure?.irm_visits_id || null,
      });
      if (res?.flag === 1 || res?.statusCode === 201) {
        setFolliculometries(prev => [res.data, ...prev]);
        setCreateOpen(false);
        Swal.fire({ icon: 'success', title: 'Created', text: 'Folliculometry session started.', timer: 2000, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: res?.message || 'Failed.' });
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e?.message || 'Failed.' });
    } finally {
      setCreating(false);
    }
  };

  // ── Change status ──────────────────────────────────────────────────
  const handleSaveStatus = async () => {
    setSavingStatus(true);
    try {
      const res = await api.ivf.updateFolliculometryStatus(statusDialog.id, statusDialog.currentStatus);
      if (res?.flag === 1 || res?.statusCode === 200) {
        setFolliculometries(prev => prev.map(f => f.id === statusDialog.id ? { ...f, status: statusDialog.currentStatus } : f));
        setStatusDialog({ open: false, id: null, currentStatus: '' });
        Swal.fire({ icon: 'success', title: 'Updated', timer: 1800, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: res?.message || 'Failed.' });
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e?.message });
    } finally {
      setSavingStatus(false);
    }
  };

  // ── Delete folliculometry ──────────────────────────────────────────
  const handleDelete = async (f) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete Folliculometry?',
      text: 'This will delete all related day records. Cannot be undone.',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await api.ivf.deleteFolliculometry(f.id);
      if (res?.flag === 1 || res?.statusCode === 200) {
        setFolliculometries(prev => prev.filter(x => x.id !== f.id));
        Swal.fire({ icon: 'success', title: 'Deleted', timer: 1800, showConfirmButton: false });
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e?.message });
    }
  };

  // ── Day dialog ─────────────────────────────────────────────────────
  const openAddDay = (folliculometryId) => {
    const d = emptyDay();
    setDayForm(d);
    setDayGrid(recordsToGrid([]));
    setDayDialog({ open: true, folliculometryId, dayId: null });
  };

  const openEditDay = (folliculometryId, day) => {
    setDayForm({
      date: day.date || '',
      cycle_day: day.cycle_day ? String(day.cycle_day).replace(/^Day\s*/i, '') : '',
      endometrium: day.endometrium || '',
      cycle_type: day.cycle_type || '',
      agonist_value: day.agonist_value || '',
      antagonist_value: day.antagonist_value || '',
      actions: day.actions || '',
      comments: day.comments || '',
      records: day.records || [],
      params: day.params || [],
      drugs: (day.drugs || []).map(d => ({
        drug_type: d.drug_type || '',
        drug_name: d.drug_name || '',
        irm_ivf_drug_master_id: d.irm_ivf_drug_master_id || null,
        dose: d.dose || '',
        route: d.route || '',
        days: d.days || '',
        times: d.times || [],
      })),
    });
    setDayGrid(recordsToGrid(day.records || []));
    setDayDialog({ open: true, folliculometryId, dayId: day.id });
  };

  // Opens the CS Report (Comparison Sheet) full screen, sliding in from the right —
  // used by the ribbon tab inside the Add/Edit Day dialog.
  const openComparisonSheetFullScreen = async (folliculometryId) => {
    if (!folliculometryId) return;
    const f = folliculometries.find(x => x.id === folliculometryId) || { id: folliculometryId };
    let days = daysMap[folliculometryId];
    if (!days) {
      days = await loadDays(folliculometryId);
    }
    setComparisonSheet({ open: true, folliculometry: f, days: days || [], fullScreen: true });
  };

  const handleGridChange = (size, side, value) => {
    setDayGrid(prev => ({ ...prev, [size]: { ...prev[size], [side]: value } }));
  };

  const handleAddParamByType = (type, member) => {
    setDayForm(prev => ({
      ...prev,
      params: [...prev.params, {
        param_type: type,
        param_name: '',
        param_value: '',
        irm_param_groups_id: member?.irm_param_groups_id || null,
        irm_param_group_members_id: member?.id || null,
        irm_param_group_member_options_id: null,
      }],
    }));
  };

  const handleParamChange = (idx, field, value, optionId) => {
    setDayForm(prev => {
      const params = [...prev.params];
      params[idx] = { ...params[idx], [field]: value };
      if (field === 'param_name') {
        params[idx].irm_param_group_member_options_id = optionId || null;
      }
      return { ...prev, params };
    });
  };

  const handleRemoveParam = (idx) => {
    setDayForm(prev => ({ ...prev, params: prev.params.filter((_, i) => i !== idx) }));
  };

  const handleAddDrug = () => {
    setDayForm(prev => ({ ...prev, drugs: [...prev.drugs, emptyDrug()] }));
  };

  const handleDrugChange = (idx, field, value, optionId) => {
    setDayForm(prev => {
      const drugs = [...prev.drugs];
      drugs[idx] = { ...drugs[idx], [field]: value };
      if (field === 'drug_name') {
        drugs[idx].irm_ivf_drug_master_id = optionId || null;
      }
      if (field === 'drug_type') {
        // Type changed — dependent drug name/route selections no longer apply
        drugs[idx].drug_name = '';
        drugs[idx].irm_ivf_drug_master_id = null;
        drugs[idx].route = '';
      }
      return { ...prev, drugs };
    });
  };

  const handleRemoveDrug = (idx) => {
    setDayForm(prev => ({ ...prev, drugs: prev.drugs.filter((_, i) => i !== idx) }));
  };

  const handleSaveDay = async () => {
    if (!dayForm.date) {
      Swal.fire({ icon: 'warning', title: 'Date required', text: 'Please select a date.' });
      return;
    }
    setSavingDay(true);
    try {
      const payload = {
        date: dayForm.date,
        cycle_day: dayForm.cycle_day || undefined,
        endometrium: dayForm.endometrium || undefined,
        cycle_type: dayForm.cycle_type || undefined,
        agonist_value: dayForm.agonist_value || undefined,
        antagonist_value: dayForm.antagonist_value || undefined,
        actions: dayForm.actions || undefined,
        comments: dayForm.comments || undefined,
        records: gridToRecords(dayGrid),
        params: dayForm.params.filter(p => p.param_name),
        drugs: dayForm.drugs.filter(d => d.drug_type && d.drug_name),
      };

      let res;
      if (dayDialog.dayId) {
        res = await api.ivf.updateFolliculometryDay(dayDialog.dayId, payload);
      } else {
        res = await api.ivf.createFolliculometryDay(dayDialog.folliculometryId, payload);
      }

      if (res?.flag === 1 || res?.statusCode === 200 || res?.statusCode === 201) {
        await loadDays(dayDialog.folliculometryId);
        setDayDialog({ open: false, folliculometryId: null, dayId: null });
        Swal.fire({ icon: 'success', title: 'Saved', timer: 1800, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: res?.message || 'Failed.' });
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e?.message });
    } finally {
      setSavingDay(false);
    }
  };

  const handleDeleteDay = async (folliculometryId, dayId) => {
    const result = await Swal.fire({
      icon: 'warning', title: 'Delete Day?',
      showCancelButton: true, confirmButtonColor: '#dc3545', confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await api.ivf.deleteFolliculometryDay(dayId);
      if (res?.flag === 1 || res?.statusCode === 200) {
        setDaysMap(prev => ({ ...prev, [folliculometryId]: prev[folliculometryId]?.filter(d => d.id !== dayId) || [] }));
        Swal.fire({ icon: 'success', title: 'Deleted', timer: 1500, showConfirmButton: false });
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e?.message });
    }
  };

  // ── Oocyte Collection handlers ─────────────────────────────────────
  const openOocyteDialog = async (folliculometryId) => {
    setOocyteDialog({ open: true, folliculometryId });
    setOocyteForm(emptyOocyte());
    setOocyteEditId(null);
    if (!oocyteCollections[folliculometryId]) {
      setLoadingOocyte(prev => ({ ...prev, [folliculometryId]: true }));
      try {
        const res = await api.ivf.getOocyteCollections(folliculometryId);
        if (res?.flag === 1 || res?.statusCode === 200) {
          setOocyteCollections(prev => ({ ...prev, [folliculometryId]: res.data || [] }));
        }
      } catch (e) { console.error(e); }
      finally { setLoadingOocyte(prev => ({ ...prev, [folliculometryId]: false })); }
    }
  };

  const handleOocyteEdit = (record) => {
    setOocyteEditId(record.id);
    setOocyteForm({
      ultrasound: record.ultrasound || '',
      vaginal: record.vaginal || '',
      others: record.others || '',
      ga: record.ga || '',
      date: record.date || '',
      start_of_operation: record.start_of_operation || '',
      duration: record.duration || '',
      surgeon: record.surgeon || '',
      anaesthetist: record.anaesthetist || '',
      embryologist: record.embryologist || '',
      leading_follicle_size_right: record.leading_follicle_size_right || '',
      leading_follicle_size_left: record.leading_follicle_size_left || '',
      follicles_punctured_right: record.follicles_punctured_right ?? '',
      follicles_punctured_left: record.follicles_punctured_left ?? '',
      eggs_retrieved_right: record.eggs_retrieved_right ?? '',
      eggs_retrieved_left: record.eggs_retrieved_left ?? '',
      ovarian_stabs_right: record.ovarian_stabs_right ?? '',
      ovarian_stabs_left: record.ovarian_stabs_left ?? '',
      abdominal_stabs_right: record.abdominal_stabs_right ?? '',
      abdominal_stabs_left: record.abdominal_stabs_left ?? '',
      bladder_remarks: record.bladder_remarks || '',
      comments: record.comments || '',
      complications: record.complications || '',
      embryos_frozen_stage: record.embryos_frozen_stage || '',
    });
  };

  const handleOocyteSave = async () => {
    const { folliculometryId } = oocyteDialog;
    setSavingOocyte(true);
    try {
      // ── Validate mandatory sections ────────────────────────────────────
      
      // 1. Route section - at least one must be filled
      const hasRoute = oocyteForm.ultrasound || oocyteForm.vaginal || oocyteForm.others || oocyteForm.ga;
      if (!hasRoute) {
        Swal.fire({ icon: 'warning', title: 'Validation Error', text: 'Please select at least one Route (USG, Routes, Others, or GA).' });
        return;
      }

      // 2. Operation Details - all must be filled
      if (!oocyteForm.date || !oocyteForm.start_of_operation || !oocyteForm.duration) {
        const missing = [];
        if (!oocyteForm.date) missing.push('Date');
        if (!oocyteForm.start_of_operation) missing.push('Start of Operation');
        if (!oocyteForm.duration) missing.push('Duration');
        Swal.fire({ icon: 'warning', title: 'Validation Error', text: `Please fill all Operation Details: ${missing.join(', ')}.` });
        return;
      }

      // 3. Team - all must be filled
      if (!oocyteForm.surgeon || !oocyteForm.anaesthetist || !oocyteForm.embryologist) {
        const missing = [];
        if (!oocyteForm.surgeon) missing.push('Surgeon');
        if (!oocyteForm.anaesthetist) missing.push('Anaesthetist');
        if (!oocyteForm.embryologist) missing.push('Embryologist');
        Swal.fire({ icon: 'warning', title: 'Validation Error', text: `Please fill all Team members: ${missing.join(', ')}.` });
        return;
      }

      const payload = {};
      Object.entries(oocyteForm).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) payload[k] = v;
      });
      let res;
      if (oocyteEditId) {
        res = await api.ivf.updateOocyteCollection(oocyteEditId, payload);
      } else {
        res = await api.ivf.createOocyteCollection(folliculometryId, payload);
      }
      if (res?.flag === 1 || res?.statusCode === 200 || res?.statusCode === 201) {
        const refreshed = await api.ivf.getOocyteCollections(folliculometryId);
        setOocyteCollections(prev => ({ ...prev, [folliculometryId]: refreshed?.data || [] }));
        setOocyteForm(emptyOocyte());
        setOocyteEditId(null);
        Swal.fire({ icon: 'success', title: 'Saved', timer: 1500, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: res?.message || 'Failed.' });
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e?.message });
    } finally { setSavingOocyte(false); }
  };

  const handleOocyteDelete = async (folliculometryId, id) => {
    const result = await Swal.fire({ icon: 'warning', title: 'Delete?', showCancelButton: true, confirmButtonColor: '#dc3545', confirmButtonText: 'Delete' });
    if (!result.isConfirmed) return;
    try {
      const res = await api.ivf.deleteOocyteCollection(id);
      if (res?.flag === 1 || res?.statusCode === 200) {
        setOocyteCollections(prev => ({ ...prev, [folliculometryId]: (prev[folliculometryId] || []).filter(r => r.id !== id) }));
        if (oocyteEditId === id) { setOocyteEditId(null); setOocyteForm(emptyOocyte()); }
        Swal.fire({ icon: 'success', title: 'Deleted', timer: 1500, showConfirmButton: false });
      }
    } catch (e) { Swal.fire({ icon: 'error', title: 'Error', text: e?.message }); }
  };

  // ── UI ─────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LoaderOverlay open={loading} />

      <MDBox p={3} sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        {/* Breadcrumb */}
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" sx={{ color: '#7b809a' }} />}
            sx={{ backgroundColor: 'white', px: 2, py: 1.5, borderRadius: 1, border: '1px solid #e9ecef' }}
          >
            <Link underline="hover" sx={{ display: 'flex', alignItems: 'center', color: '#7b809a', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, '&:hover': { color: primaryColor } }} onClick={() => navigate('/dashboard')}>
              <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} /> Dashboard
            </Link>
            <Link underline="hover" sx={{ display: 'flex', alignItems: 'center', color: '#7b809a', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, '&:hover': { color: primaryColor } }} onClick={() => navigate('/ivf')}>
              <ScienceIcon sx={{ mr: 0.5, fontSize: 16 }} /> IVF Procedures
            </Link>
            <Link underline="hover" sx={{ display: 'flex', alignItems: 'center', color: '#7b809a', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, '&:hover': { color: primaryColor } }} onClick={() => navigate(`/ivf/initiation/${procedureId}`, { state: { procedure, patient } })}>
              Procedure Initiation
            </Link>
            <Typography sx={{ display: 'flex', alignItems: 'center', color: primaryColor, fontWeight: 700, fontSize: '0.875rem' }}>
              <BiotechIcon sx={{ mr: 0.5, fontSize: 16 }} /> Folliculometry
            </Typography>
          </Breadcrumbs>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/ivf/initiation/${procedureId}`, { state: { procedure, patient } })}
            sx={{ color: '#7b809a', textTransform: 'none', fontWeight: 500, '&:hover': { color: primaryColor }, ml: 2 }}>
            Back
          </Button>
        </MDBox>

        {/* Header Card */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 2, border: '1px solid #e9ecef', p: 2.5, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box sx={{ width: 48, height: 48, borderRadius: '12px', backgroundColor: `rgba(0,121,142,0.1)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BiotechIcon sx={{ color: primaryColor, fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, color: textColor, fontSize: '1.05rem' }}>
                IVF Folliculometry
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: secondaryColor }}>
                {patient?.full_name || patient?.name || 'Patient'} · Procedure: {procedureCode}
              </Typography>
            </Box>
          </Box>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{
                backgroundColor: `${primaryColor} !important`,
                color: 'white !important',
                textTransform: 'none', fontWeight: 700,
                borderRadius: '8px', px: 2.5,
              }}
            >
              Start Folliculometry
            </Button>
          )}
        </Box>

        {/* Folliculometry List */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress sx={{ color: primaryColor }} /></Box>
        ) : folliculometries.length === 0 ? (
          <Box textAlign="center" py={8} sx={{ backgroundColor: 'white', borderRadius: 2, border: '1px dashed #dee2e6' }}>
            <BiotechIcon sx={{ fontSize: 48, color: '#adb5bd', mb: 1.5 }} />
            <Typography sx={{ fontWeight: 700, color: textColor, mb: 0.5 }}>No Folliculometry Sessions</Typography>
            <Typography sx={{ color: secondaryColor, fontSize: '0.875rem' }}>Click &ldquo;Start Folliculometry&rdquo; to begin a new session.</Typography>
          </Box>
        ) : (
          folliculometries.map((f) => {
            const st = STATUS_STYLE[f.status] || STATUS_STYLE.ONGOING;
            const days = daysMap[f.id] || [];
            const isExpanded = expandedId === f.id;
            return (
              <Box key={f.id} sx={{ backgroundColor: 'white', borderRadius: 2, border: '1.5px solid #e9ecef', mb: 2, overflow: 'hidden' }}>
                {/* Session header */}
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', '&:hover': { backgroundColor: '#f7fbfc' } }}
                  onClick={() => handleExpand(f.id)}
                >
                  <ExpandMoreIcon sx={{ color: secondaryColor, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: '0.2s', fontSize: 20 }} />
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography sx={{ fontWeight: 700, color: textColor, fontSize: '0.9rem' }}>
                        Folliculometry #{f.id}
                      </Typography>
                      <Chip
                        label={f.status}
                        size="small"
                        sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, backgroundColor: st.bg, color: st.color, border: `1px solid ${st.border}`, '& .MuiChip-label': { px: 0.9 } }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: secondaryColor }}>
                      Started: {f.created_at ? new Date(f.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      {' · '}{(daysMap[f.id] || []).length} day(s) recorded
                    </Typography>
                  </Box>
                  {(() => {
                    // Sequential list of row actions — drives both the icon row and the ⋮ dropdown, so they never drift out of sync
                    const rowActions = [
                      {
                        key: 'print',
                        show: true,
                        label: 'Print Report',
                        icon: PrintIcon,
                        color: '#212529', bg: 'rgba(0,0,0,0.06)', bgHover: 'rgba(0,0,0,0.14)',
                        onClick: async () => {
                          let days = daysMap[f.id];
                          if (!days) days = await loadDays(f.id);
                          setPrintDialog({ open: true, folliculometry: f, days: days || [] });
                        },
                      },
                      {
                        key: 'cs',
                        show: true,
                        label: 'CS Report',
                        icon: ArticleIcon,
                        color: '#e65100', bg: 'rgba(230,81,0,0.06)', bgHover: 'rgba(230,81,0,0.14)',
                        onClick: async () => {
                          let days = daysMap[f.id];
                          if (!days) days = await loadDays(f.id);
                          setComparisonSheet({ open: true, folliculometry: f, days: days || [], fullScreen: false });
                        },
                      },
                      {
                        key: 'addDay',
                        show: canEdit && f.status !== 'COMPLETED',
                        label: 'Add Day',
                        icon: AddIcon,
                        color: primaryColor, bg: 'rgba(0,121,142,0.06)', bgHover: 'rgba(0,121,142,0.14)',
                        onClick: () => { setExpandedId(f.id); openAddDay(f.id); },
                      },
                      {
                        key: 'changeStatus',
                        show: canEdit && f.status !== 'COMPLETED',
                        label: 'Change Status',
                        icon: SwapVertIcon,
                        color: '#7b1fa2', bg: 'rgba(123,31,162,0.06)', bgHover: 'rgba(123,31,162,0.14)',
                        onClick: () => setStatusDialog({ open: true, id: f.id, currentStatus: f.status }),
                      },
                      {
                        key: 'oocyte',
                        show: (daysMap[f.id] || []).length > 0,
                        label: 'Oocyte Collection',
                        icon: EggAltIcon,
                        color: '#ff6f00', bg: 'rgba(255,111,0,0.08)', bgHover: 'rgba(255,111,0,0.18)',
                        onClick: () => openOocyteDialog(f.id),
                      },
                      {
                        key: 'fetMonitoring',
                        show: canEdit,
                        label: 'FET Monitoring Data',
                        icon: EggAltIcon,
                        color: '#0277bd', bg: 'rgba(2,119,189,0.08)', bgHover: 'rgba(2,119,189,0.18)',
                        onClick: () => setFetMonitoringModalOpen({ open: true, procedureId: parseInt(procedureId, 10) }),
                      },
                      {
                        key: 'embroTransfer',
                        show: (daysMap[f.id] || []).length > 0,
                        label: 'Embro Transfer Data',
                        icon: BiotechIcon,
                        color: '#6a1b9a', bg: 'rgba(106,27,154,0.08)', bgHover: 'rgba(106,27,154,0.18)',
                        onClick: () => setEmbroTransferModalOpen({ open: true, folliculometryId: f.id }),
                      },
                      {
                        key: 'transferOutcome',
                        show: canEdit,
                        label: 'Transfer Outcome',
                        icon: ArticleIcon,
                        color: '#4caf50', bg: 'rgba(76,175,80,0.08)', bgHover: 'rgba(76,175,80,0.18)',
                        onClick: () => setEmbryoOutcomeModalOpen({ open: true, procedureId: parseInt(procedureId, 10) }),
                      },
                      {
                        key: 'finalOutcome',
                        show: canEdit,
                        label: 'Final Outcome',
                        icon: SaveIcon,
                        color: '#ff9800', bg: 'rgba(255,152,0,0.08)', bgHover: 'rgba(255,152,0,0.18)',
                        onClick: () => setFinalOutcomeModalOpen({ open: true, procedureId: parseInt(procedureId, 10) }),
                      },
                      {
                        key: 'delete',
                        show: canDelete && f.status !== 'COMPLETED',
                        label: 'Delete',
                        icon: DeleteIcon,
                        color: '#d32f2f', bg: 'rgba(211,47,47,0.08)', bgHover: 'rgba(211,47,47,0.18)',
                        onClick: () => handleDelete(f),
                      },
                    ].filter(a => a.show);

                    return (
                      <Box display="flex" alignItems="center" gap={0.5} onClick={e => e.stopPropagation()}>
                        {rowActions.map(a => (
                          <Tooltip key={a.key} title={a.label}>
                            <IconButton size="small" onClick={a.onClick}
                              sx={{ color: a.color, backgroundColor: a.bg, '&:hover': { backgroundColor: a.bgHover }, width: 30, height: 30 }}>
                              <a.icon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        ))}
                        <Tooltip title="More Actions">
                          <IconButton size="small"
                            onClick={(e) => setActionMenu({ anchorEl: e.currentTarget, folliculometryId: f.id })}
                            sx={{ color: '#495057', backgroundColor: 'rgba(0,0,0,0.05)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' }, width: 30, height: 30 }}>
                            <MoreVertIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Menu
                          anchorEl={actionMenu.anchorEl}
                          open={Boolean(actionMenu.anchorEl) && actionMenu.folliculometryId === f.id}
                          onClose={() => setActionMenu({ anchorEl: null, folliculometryId: null })}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                          {rowActions.map(a => (
                            <MenuItem key={a.key} onClick={() => { a.onClick(); setActionMenu({ anchorEl: null, folliculometryId: null }); }}>
                              <ListItemIcon><a.icon fontSize="small" sx={{ color: a.color }} /></ListItemIcon>
                              <ListItemText>{a.label}</ListItemText>
                            </MenuItem>
                          ))}
                        </Menu>
                      </Box>
                    );
                  })()}
                </Box>

                {/* Day list */}
                {isExpanded && (
                  <Box sx={{ borderTop: '1px solid #e9ecef', px: 2, py: 1.5 }}>
                    {loadingDays[f.id] ? (
                      <Box display="flex" justifyContent="center" py={3}><CircularProgress size={22} sx={{ color: primaryColor }} /></Box>
                    ) : days.length === 0 ? (
                      <Typography sx={{ color: secondaryColor, fontSize: '0.8rem', py: 2, textAlign: 'center' }}>
                        No days recorded yet. Click + to add.
                      </Typography>
                    ) : (
                      days.map(day => <DayCard key={day.id} day={day} folliculometryId={f.id} canEdit={canEdit} canDelete={canDelete}
                        folliculometryStatus={f.status}
                        onEdit={() => openEditDay(f.id, day)}
                        onDelete={() => handleDeleteDay(f.id, day.id)}
                        primaryColor={primaryColor} textColor={textColor} secondaryColor={secondaryColor}
                      />)
                    )}
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </MDBox>

      {/* ── Create Folliculometry Dialog ── */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
        <Box sx={{ background: `linear-gradient(135deg, ${primaryColor} 0%, #005f75 100%)`, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <BiotechIcon sx={{ color: 'white', fontSize: 22 }} />
            <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.95rem' }}>Start Folliculometry</Typography>
          </Box>
          <IconButton size="small" onClick={() => !creating && setCreateOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </Box>
        <Box sx={{ px: 3, py: 2.5 }}>
          <Typography sx={{ fontSize: '0.85rem', color: textColor, mb: 2 }}>
            A new folliculometry session will be started for this procedure with status <strong>ONGOING</strong>.
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button size="small" variant="outlined" onClick={() => setCreateOpen(false)} disabled={creating}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '7px', borderColor: '#dee2e6', color: '#6c757d' }}>Cancel</Button>
            <Button size="small" variant="contained" onClick={handleCreate} disabled={creating}
              startIcon={creating ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <AddIcon sx={{ fontSize: 15 }} />}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '7px', backgroundColor: `${primaryColor} !important`, color: 'white !important' }}>
              Start
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ── Change Status Dialog ── */}
      <Dialog open={statusDialog.open} onClose={() => !savingStatus && setStatusDialog({ open: false, id: null, currentStatus: '' })}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <SwapVertIcon sx={{ color: 'white', fontSize: 22 }} />
            <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.95rem' }}>Change Status</Typography>
          </Box>
          <IconButton size="small" onClick={() => !savingStatus && setStatusDialog({ open: false, id: null, currentStatus: '' })} sx={{ color: 'rgba(255,255,255,0.7)' }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </Box>
        <Box sx={{ px: 3, py: 2.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: textColor, display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.72rem' }}>New Status</Typography>
          <Select fullWidth size="small" value={statusDialog.currentStatus}
            onChange={e => setStatusDialog(prev => ({ ...prev, currentStatus: e.target.value }))}
            sx={{ mb: 2.5, height: 40, fontSize: '0.875rem' }}>
            {FOLLICULOMETRY_STATUSES.map(s => <MenuItem key={s} value={s} sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{s}</MenuItem>)}
          </Select>
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button size="small" variant="outlined" onClick={() => setStatusDialog({ open: false, id: null, currentStatus: '' })} disabled={savingStatus}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '7px', borderColor: '#dee2e6', color: '#6c757d' }}>Cancel</Button>
            <Button size="small" variant="contained" onClick={handleSaveStatus} disabled={savingStatus}
              startIcon={savingStatus ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SaveIcon sx={{ fontSize: 15 }} />}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '7px', backgroundColor: '#7b1fa2 !important', color: 'white !important' }}>
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ── Add/Edit Day Dialog ── */}
      <Dialog
        open={dayDialog.open}
        onClose={() => !savingDay && setDayDialog({ open: false, folliculometryId: null, dayId: null })}
        maxWidth="xl" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden', maxHeight: '94vh', position: 'relative' } }}
      >
        {/* Ribbon tab — opens the CS Report (Comparison Sheet), sliding in full screen */}
        <Tooltip title="CS Report" placement="left">
          <Box
            onClick={() => openComparisonSheetFullScreen(dayDialog.folliculometryId)}
            sx={{
              position: 'absolute',
              top: '50%',
              right: 0,
              transform: 'translateY(-50%)',
              width: 26,
              height: 80,
              backgroundColor: '#e65100',
              borderRadius: '8px 0 0 8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '-2px 0 6px rgba(0,0,0,0.18)',
              zIndex: 5,
              '&:hover': { backgroundColor: '#bf360c' },
            }}
          >
            <ArticleIcon sx={{ color: '#fff', fontSize: 15 }} />
          </Box>
        </Tooltip>

        <Box sx={{ background: `linear-gradient(135deg, ${primaryColor} 0%, #005f75 100%)`, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <BiotechIcon sx={{ color: 'white', fontSize: 22 }} />
            <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.95rem' }}>
              {dayDialog.dayId ? 'Edit Day' : 'Add Day'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => !savingDay && setDayDialog({ open: false, folliculometryId: null, dayId: null })} sx={{ color: 'rgba(255,255,255,0.7)' }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </Box>

        <Box sx={{ overflowY: 'auto', flex: 1, px: 3, py: 2 }}>
          {/* Basic fields */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: textColor, fontSize: '0.72rem', textTransform: 'uppercase' }}>Date *</Typography>
              <MDDatePicker
                value={dayForm.date}
                onChange={val => setDayForm(p => ({ ...p, date: val || '' }))}
                disableFuture
                textFieldProps={{ fullWidth: true, size: 'small' }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: textColor, fontSize: '0.72rem', textTransform: 'uppercase' }}>Cycle Day</Typography>
              <Select fullWidth size="small" displayEmpty value={dayForm.cycle_day}
                onChange={e => setDayForm(p => ({ ...p, cycle_day: e.target.value }))}
                sx={{ height: 40 }}>
                <MenuItem value=""><em>—</em></MenuItem>
                {Array.from({ length: 30 }, (_, i) => i + 1).map(n => (
                  <MenuItem key={n} value={String(n)}>{n}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: textColor, fontSize: '0.72rem', textTransform: 'uppercase' }}>Endometrium</Typography>
              <TextField fullWidth size="small" placeholder="e.g. 8mm, Triple line" value={dayForm.endometrium}
                onChange={e => setDayForm(p => ({ ...p, endometrium: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: textColor, fontSize: '0.72rem', textTransform: 'uppercase' }}>Cycle Type</Typography>
              <Select fullWidth size="small" displayEmpty value={dayForm.cycle_type}
                onChange={e => setDayForm(p => ({
                  ...p,
                  cycle_type: e.target.value,
                  agonist_value: e.target.value === 'Antagonist' ? '' : p.agonist_value,
                  antagonist_value: e.target.value === 'Agonist' ? '' : p.antagonist_value,
                }))}
                sx={{ height: 40 }}>
                <MenuItem value=""><em>—</em></MenuItem>
                {CYCLE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </Grid>
            {dayForm.cycle_type === 'Agonist' && (
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: textColor, fontSize: '0.72rem', textTransform: 'uppercase' }}>Agonist Value</Typography>
                <TextField fullWidth size="small" value={dayForm.agonist_value}
                  onChange={e => setDayForm(p => ({ ...p, agonist_value: e.target.value }))} />
              </Grid>
            )}
            {dayForm.cycle_type === 'Antagonist' && (
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: textColor, fontSize: '0.72rem', textTransform: 'uppercase' }}>Antagonist Value</Typography>
                <TextField fullWidth size="small" value={dayForm.antagonist_value}
                  onChange={e => setDayForm(p => ({ ...p, antagonist_value: e.target.value }))} />
              </Grid>
            )}
          </Grid>

          <Divider sx={{ mb: 2 }} />

          {/* Follicle Grid */}
          <Typography sx={{ fontWeight: 700, color: textColor, fontSize: '0.85rem', mb: 1.5 }}>
            Follicle Count (Right / Left)
          </Typography>
          <FollicleGrid grid={dayGrid} onChange={handleGridChange} />

          <Divider sx={{ my: 2 }} />

          {/* ── Advice Section: SERUM / Drugs / Trigger + Actions & Comments ── */}
          <Box sx={{ border: '1px solid #e3e8ee', borderRadius: '10px', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.1, backgroundColor: `${primaryColor}12`, borderBottom: `1px solid ${primaryColor}30` }}>
              <MedicalInformationIcon sx={{ fontSize: 18, color: primaryColor }} />
              <Typography sx={{ fontWeight: 700, color: primaryColor, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Advice
              </Typography>
            </Box>

            <Box sx={{ p: 2 }}>
              {/* Serum / Trigger Params */}
              <Grid container spacing={1.5}>
                {[
                  { type: 'SERM',    label: 'Serum',   icon: BloodtypeIcon, color: '#1565c0' },
                  { type: 'TRIGGER', label: 'Trigger', icon: BoltIcon,      color: '#e65100' },
                ].map(({ type, label, icon: TypeIcon, color }) => {
                  const member = paramMembers[type];
                  const options = (member?.param_group_member_options || []).map(o => ({
                    id: o.id,
                    label: o.option_value,
                  }));
                  const rows = dayForm.params
                    .map((p, i) => ({ ...p, _idx: i }))
                    .filter(p => p.param_type === type);
                  return (
                    <Grid item xs={12} md={6} key={type}>
                      <Box sx={{ height: '100%', border: `1px solid ${color}30`, borderTop: `3px solid ${color}`, borderRadius: '8px', backgroundColor: '#fff', p: 1.5 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <TypeIcon sx={{ fontSize: 16, color }} />
                            <Typography sx={{ fontWeight: 700, color, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                              {label}
                            </Typography>
                          </Box>
                          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                            onClick={() => handleAddParamByType(type, member)}
                            sx={{ textTransform: 'none', fontSize: '0.72rem', color, minWidth: 0, py: 0.1, px: 0.75, '&:hover': { backgroundColor: `${color}10` } }}>
                            Add
                          </Button>
                        </Box>
                        {rows.length === 0 ? (
                          <Box sx={{ py: 1.5, textAlign: 'center', backgroundColor: `${color}08`, borderRadius: '6px' }}>
                            <Typography sx={{ color: secondaryColor, fontSize: '0.72rem', fontStyle: 'italic' }}>None added</Typography>
                          </Box>
                        ) : (
                          rows.map(p => (
                            <ParamRow key={p._idx} param={p} options={options}
                              onChange={(field, value, optionId) => handleParamChange(p._idx, field, value, optionId)}
                              onRemove={() => handleRemoveParam(p._idx)} />
                          ))
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Drugs — structured advice */}
              <Box sx={{ border: '1px solid #6a1b9a30', borderTop: '3px solid #6a1b9a', borderRadius: '8px', backgroundColor: '#fff', p: 1.5 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <MedicationIcon sx={{ fontSize: 16, color: '#6a1b9a' }} />
                    <Typography sx={{ fontWeight: 700, color: '#6a1b9a', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Drugs
                    </Typography>
                  </Box>
                  <Button size="small" startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                    onClick={handleAddDrug}
                    sx={{ textTransform: 'none', fontSize: '0.72rem', color: '#6a1b9a', minWidth: 0, py: 0.1, px: 0.75, '&:hover': { backgroundColor: '#6a1b9a10' } }}>
                    Add Drug
                  </Button>
                </Box>
                {dayForm.drugs.length === 0 ? (
                  <Box sx={{ py: 1.5, textAlign: 'center', backgroundColor: '#6a1b9a08', borderRadius: '6px' }}>
                    <Typography sx={{ color: secondaryColor, fontSize: '0.72rem', fontStyle: 'italic' }}>None added</Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e9ecef', borderRadius: '6px', overflow: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 860 }}>
                      <TableBody>
                        <TableRow sx={{ backgroundColor: '#f8f5fb' }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#6a1b9a', textTransform: 'uppercase', width: 130 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#6a1b9a', textTransform: 'uppercase', minWidth: 190 }}>Drug Name</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#6a1b9a', textTransform: 'uppercase', width: 100 }}>Dose</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#6a1b9a', textTransform: 'uppercase', width: 140 }}>Route</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#6a1b9a', textTransform: 'uppercase', width: 80 }}>Days</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', color: '#6a1b9a', textTransform: 'uppercase', minWidth: 200 }}>Time</TableCell>
                          <TableCell sx={{ width: 36 }} />
                        </TableRow>
                        {dayForm.drugs.map((d, idx) => (
                          <DrugRow key={idx} drug={d} drugOptions={drugMaster}
                            onChange={(field, value, optionId) => handleDrugChange(idx, field, value, optionId)}
                            onRemove={() => handleRemoveDrug(idx)} />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Actions & Comments */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <AssignmentIcon sx={{ fontSize: 15, color: textColor }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: textColor, fontSize: '0.72rem', textTransform: 'uppercase' }}>Actions</Typography>
                  </Box>
                  <TextField fullWidth multiline rows={3} size="small" placeholder="Clinical actions taken…"
                    value={dayForm.actions} onChange={e => setDayForm(p => ({ ...p, actions: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <CommentIcon sx={{ fontSize: 15, color: textColor }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: textColor, fontSize: '0.72rem', textTransform: 'uppercase' }}>Comments</Typography>
                  </Box>
                  <TextField fullWidth multiline rows={3} size="small" placeholder="Additional comments…"
                    value={dayForm.comments} onChange={e => setDayForm(p => ({ ...p, comments: e.target.value }))} />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e9ecef', display: 'flex', justifyContent: 'flex-end', gap: 1, flexShrink: 0 }}>
          <Button size="small" variant="outlined" onClick={() => setDayDialog({ open: false, folliculometryId: null, dayId: null })} disabled={savingDay}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '7px', borderColor: '#dee2e6', color: '#6c757d' }}>Cancel</Button>
          <Button size="small" variant="contained" onClick={handleSaveDay} disabled={savingDay}
            startIcon={savingDay ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SaveIcon sx={{ fontSize: 15 }} />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '7px', backgroundColor: `${primaryColor} !important`, color: 'white !important' }}>
            Save Day
          </Button>
        </Box>
      </Dialog>

      {/* ── Oocyte Collection Dialog ── */}
      <Dialog open={oocyteDialog.open} onClose={() => !savingOocyte && setOocyteDialog({ open: false, folliculometryId: null })} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
        {/* Header */}
        <Box sx={{ background: 'linear-gradient(135deg, #e65100 0%, #bf360c 100%)', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <EggAltIcon sx={{ color: 'white', fontSize: 22 }} />
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
              Oocyte Collection — Folliculometry #{oocyteDialog.folliculometryId}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setOocyteDialog({ open: false, folliculometryId: null })}
            sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 3, maxHeight: '80vh', overflowY: 'auto' }}>
          {/* Records list */}
          {loadingOocyte[oocyteDialog.folliculometryId] ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress size={22} sx={{ color: '#e65100' }} /></Box>
          ) : (oocyteCollections[oocyteDialog.folliculometryId] || []).length > 0 ? (
            <Box mb={3}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: textColor, mb: 1.5 }}>Existing Records</Typography>
              {(oocyteCollections[oocyteDialog.folliculometryId] || []).map(rec => (
                <Box key={rec.id} sx={{ border: '1px solid #ffe0b2', borderRadius: '8px', p: 1.5, mb: 1, backgroundColor: '#fff8f5', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box flex={1}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#e65100' }}>
                      Record #{rec.id} {rec.date ? `· ${new Date(rec.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: secondaryColor }}>
                      Eggs R: {rec.eggs_retrieved_right ?? '—'} · Eggs L: {rec.eggs_retrieved_left ?? '—'}
                      {rec.surgeon ? ` · Surgeon: ${rec.surgeon}` : ''}
                      {rec.ga ? ` · GA: ${rec.ga}` : ''}
                    </Typography>
                  </Box>
                  {canEdit && (
                    <IconButton size="small" onClick={() => handleOocyteEdit(rec)}
                      sx={{ color: primaryColor, backgroundColor: 'rgba(0,121,142,0.08)', width: 28, height: 28 }}>
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                  {canDelete && (
                    <IconButton size="small" onClick={() => handleOocyteDelete(oocyteDialog.folliculometryId, rec.id)}
                      sx={{ color: '#dc3545', backgroundColor: 'rgba(220,53,69,0.08)', width: 28, height: 28 }}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: '0.8rem', color: secondaryColor, mb: 2 }}>No records yet.</Typography>
          )}

          {/* Form */}
          {canEdit && (
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: textColor, mb: 1.5, borderTop: '1px solid #ffe0b2', pt: 2 }}>
                {oocyteEditId ? `Edit Record #${oocyteEditId}` : 'New Oocyte Collection'}
              </Typography>
              <Grid container spacing={1.5}>
                {/* Route / Method - MANDATORY */}
                <Grid item xs={12}><Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: secondaryColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Route / Method <span style={{ color: 'red' }}>*</span></Typography></Grid>
                {/* OOCYTE_USG Dropdown */}
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth select label="USG" value={oocyteForm.ultrasound}
                    onChange={e => setOocyteForm(p => ({ ...p, ultrasound: e.target.value }))}
                    sx={{
                      '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem', height: '44px' },
                      '& .MuiSelect-select': { paddingTop: '10px', paddingBottom: '10px' },
                    }}>
                    <MenuItem value="">— Select —</MenuItem>
                    {paramMembers.OOCYTE_USG?.options?.map(opt => (
                      <MenuItem key={opt.id} value={opt.option_value}>{opt.option_value}</MenuItem>
                    )) || []}
                  </TextField>
                </Grid>
                {/* VAGINAL Dropdown */}
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth select label="ROUTES" value={oocyteForm.vaginal}
                    onChange={e => setOocyteForm(p => ({ ...p, vaginal: e.target.value }))}
                    sx={{
                      '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem', height: '44px' },
                      '& .MuiSelect-select': { paddingTop: '10px', paddingBottom: '10px' },
                    }}>
                    <MenuItem value="">— Select —</MenuItem>
                    {paramMembers.VAGINAL?.options?.map(opt => (
                      <MenuItem key={opt.id} value={opt.option_value}>{opt.option_value}</MenuItem>
                    )) || []}
                  </TextField>
                </Grid>
                {/* Others TextField */}
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="OTHERS" value={oocyteForm.others}
                    onChange={e => setOocyteForm(p => ({ ...p, others: e.target.value }))}
                    placeholder="Specify other route"
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem' } }} />
                </Grid>
                {/* ANESTHESIA Dropdown (replaces GA) */}
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth select label="ANESTHESIA" value={oocyteForm.ga}
                    onChange={e => setOocyteForm(p => ({ ...p, ga: e.target.value }))}
                    sx={{
                      '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem', height: '44px' },
                      '& .MuiSelect-select': { paddingTop: '10px', paddingBottom: '10px' },
                    }}>
                    <MenuItem value="">— Select —</MenuItem>
                    {paramMembers.ANESTHESIA?.options?.map(opt => (
                      <MenuItem key={opt.id} value={opt.option_value}>{opt.option_value}</MenuItem>
                    )) || []}
                  </TextField>
                </Grid>

                {/* Operation Details - MANDATORY */}
                <Grid item xs={12}><Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: secondaryColor, textTransform: 'uppercase', letterSpacing: '0.05em', mt: 0.5 }}>Operation Details <span style={{ color: 'red' }}>*</span></Typography></Grid>
                <Grid item xs={12} sm={4}>
                  <MDDatePicker
                    label="DATE"
                    value={oocyteForm.date}
                    onChange={val => setOocyteForm(p => ({ ...p, date: val || '' }))}
                    textFieldProps={{ fullWidth: true, size: 'small' }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <MDTimePicker
                    label="START OF OPERATION"
                    value={oocyteForm.start_of_operation}
                    onChange={val => setOocyteForm(p => ({ ...p, start_of_operation: val || '' }))}
                    textFieldProps={{ fullWidth: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="DURATION (MINS)" value={oocyteForm.duration}
                    onChange={e => setOocyteForm(p => ({ ...p, duration: e.target.value }))}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem' } }} />
                </Grid>

                {/* Team - MANDATORY */}
                <Grid item xs={12}><Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: secondaryColor, textTransform: 'uppercase', letterSpacing: '0.05em', mt: 0.5 }}>Team <span style={{ color: 'red' }}>*</span></Typography></Grid>
                
                {/* Surgeon - Consultant Dropdown */}
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth select size="small" label="SURGEON" value={oocyteForm.surgeon || ''}
                    onChange={e => setOocyteForm(p => ({ ...p, surgeon: e.target.value }))}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem', height: '44px' } }}>
                    <MenuItem value="">— Select —</MenuItem>
                    {doctors
                      .filter(doctor => doctor && doctor.user_id && doctor.name)
                      .map((doctor, index) => (
                        <MenuItem key={`surgeon-${doctor.user_id}-${index}`} value={doctor.name}>
                          {doctor.name}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>

                {/* Anaesthetist - Text Field */}
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="ANAESTHETIST" value={oocyteForm.anaesthetist}
                    onChange={e => setOocyteForm(p => ({ ...p, anaesthetist: e.target.value }))}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem' } }} />
                </Grid>

                {/* Embryologist - Consultant Dropdown */}
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth select size="small" label="EMBRYOLOGIST" value={oocyteForm.embryologist || ''}
                    onChange={e => setOocyteForm(p => ({ ...p, embryologist: e.target.value }))}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem', height: '44px' } }}>
                    <MenuItem value="">— Select —</MenuItem>
                    {doctors
                      .filter(doctor => doctor && doctor.user_id && doctor.name)
                      .map((doctor, index) => (
                        <MenuItem key={`embryologist-${doctor.user_id}-${index}`} value={doctor.name}>
                          {doctor.name}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>

                {/* Follicle Size */}
                <Grid item xs={12}><Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: secondaryColor, textTransform: 'uppercase', letterSpacing: '0.05em', mt: 0.5 }}>Leading Follicle Size</Typography></Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="RIGHT (MM)" value={oocyteForm.leading_follicle_size_right}
                    onChange={e => setOocyteForm(p => ({ ...p, leading_follicle_size_right: e.target.value }))}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem' } }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="LEFT (MM)" value={oocyteForm.leading_follicle_size_left}
                    onChange={e => setOocyteForm(p => ({ ...p, leading_follicle_size_left: e.target.value }))}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem' } }} />
                </Grid>

                {/* Follicles Punctured */}
                <Grid item xs={12}><Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: secondaryColor, textTransform: 'uppercase', letterSpacing: '0.05em', mt: 0.5 }}>Follicles Punctured</Typography></Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="RIGHT" type="number" value={oocyteForm.follicles_punctured_right}
                    onChange={e => setOocyteForm(p => ({ ...p, follicles_punctured_right: e.target.value }))}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem' } }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="LEFT" type="number" value={oocyteForm.follicles_punctured_left}
                    onChange={e => setOocyteForm(p => ({ ...p, follicles_punctured_left: e.target.value }))}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem' } }} />
                </Grid>

                {/* Eggs Retrieved */}
                <Grid item xs={12}><Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: secondaryColor, textTransform: 'uppercase', letterSpacing: '0.05em', mt: 0.5 }}>Eggs Retrieved</Typography></Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="RIGHT" type="number" value={oocyteForm.eggs_retrieved_right}
                    onChange={e => setOocyteForm(p => ({ ...p, eggs_retrieved_right: e.target.value }))}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem' } }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="LEFT" type="number" value={oocyteForm.eggs_retrieved_left}
                    onChange={e => setOocyteForm(p => ({ ...p, eggs_retrieved_left: e.target.value }))}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem' } }} />
                </Grid>

                {/* Ovarian Stabs */}
                <Grid item xs={12}><Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: secondaryColor, textTransform: 'uppercase', letterSpacing: '0.05em', mt: 0.5 }}>Ovarian Stabs</Typography></Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="RIGHT" type="number" value={oocyteForm.ovarian_stabs_right}
                    onChange={e => setOocyteForm(p => ({ ...p, ovarian_stabs_right: e.target.value }))}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem' } }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="LEFT" type="number" value={oocyteForm.ovarian_stabs_left}
                    onChange={e => setOocyteForm(p => ({ ...p, ovarian_stabs_left: e.target.value }))}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem' } }} />
                </Grid>

                {/* Abdominal Stabs */}
                <Grid item xs={12}><Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: secondaryColor, textTransform: 'uppercase', letterSpacing: '0.05em', mt: 0.5 }}>Abdominal Stabs</Typography></Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="RIGHT" type="number" value={oocyteForm.abdominal_stabs_right}
                    onChange={e => setOocyteForm(p => ({ ...p, abdominal_stabs_right: e.target.value }))}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem' } }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="LEFT" type="number" value={oocyteForm.abdominal_stabs_left}
                    onChange={e => setOocyteForm(p => ({ ...p, abdominal_stabs_left: e.target.value }))}
                    sx={{ '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem' } }} />
                </Grid>

                {/* Bladder + Comments */}
                <Grid item xs={12}><Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: secondaryColor, textTransform: 'uppercase', letterSpacing: '0.05em', mt: 0.5 }}>Bladder / Notes</Typography></Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth select label="BLADDER REMARKS" value={oocyteForm.bladder_remarks}
                    onChange={e => setOocyteForm(p => ({ ...p, bladder_remarks: e.target.value }))}
                    sx={{
                      '& .MuiInputBase-root': { borderRadius: '7px', fontSize: '0.82rem', height: '44px' },
                      '& .MuiSelect-select': { paddingTop: '10px', paddingBottom: '10px' },
                    }}>
                    <MenuItem value="">—</MenuItem>
                    {BLADDER_REMARKS.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} />

                {/* Complications - Left Side */}
                <Grid item xs={12} sm={6}>
                  <Box component="fieldset" sx={{
                    border: '1px solid rgba(0,0,0,0.23)',
                    borderRadius: '7px',
                    padding: '8px',
                    '&:hover': { borderColor: 'rgba(0,0,0,0.87)' },
                    '&:focus-within': { borderColor: '#00798E', borderWidth: '2px' },
                    '& .ql-toolbar.ql-snow': { border: 'none', borderBottom: '1px solid rgba(0,0,0,0.12)', backgroundColor: '#fafafa', padding: '4px 8px', borderRadius: '7px 7px 0 0' },
                    '& .ql-container.ql-snow': { border: 'none', fontSize: '0.82rem', minHeight: '80px', borderRadius: '0 0 7px 7px' },
                    '& .ql-editor': { minHeight: '80px', padding: '6px 10px' },
                  }}>
                    <Box component="legend" sx={{ ml: '8px', px: '4px', fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.4 }}>COMPLICATIONS (OPTIONAL)</Box>
                    <ReactQuill
                      theme="snow"
                      value={oocyteForm.complications}
                      onChange={val => setOocyteForm(p => ({ ...p, complications: val }))}
                      modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']] }}
                      formats={['bold', 'italic', 'underline', 'list', 'bullet']}
                    />
                  </Box>
                </Grid>

                {/* Comments - Right Side */}
                <Grid item xs={12} sm={6}>
                  <Box component="fieldset" sx={{
                    border: '1px solid rgba(0,0,0,0.23)', borderRadius: '7px', padding: '8px', margin: 0,
                    '&:hover': { borderColor: 'rgba(0,0,0,0.87)' },
                    '&:focus-within': { borderColor: '#00798E', borderWidth: '2px' },
                    '& .ql-toolbar.ql-snow': { border: 'none', borderBottom: '1px solid rgba(0,0,0,0.12)', backgroundColor: '#fafafa', padding: '4px 8px', borderRadius: '7px 7px 0 0' },
                    '& .ql-container.ql-snow': { border: 'none', fontSize: '0.82rem', minHeight: '80px', borderRadius: '0 0 7px 7px' },
                    '& .ql-editor': { minHeight: '80px', padding: '6px 10px' },
                  }}>
                    <Box component="legend" sx={{ ml: '8px', px: '4px', fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.4 }}>COMMENTS</Box>
                    <ReactQuill
                      theme="snow"
                      value={oocyteForm.comments}
                      onChange={val => setOocyteForm(p => ({ ...p, comments: val }))}
                      modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']] }}
                      formats={['bold', 'italic', 'underline', 'list', 'bullet']}
                    />
                  </Box>
                </Grid>

                {/* Number of Oocytes Retrieved - Optional */}
                <Grid item xs={12}>
                  <TextField fullWidth
                    label="NUMBER OF OOCYTES RETRIEVED"
                    placeholder="e.g., 12"
                    value={oocyteForm.embryos_frozen_stage}
                    onChange={e => setOocyteForm(p => ({ ...p, embryos_frozen_stage: e.target.value.substring(0, 128) }))}
                    inputProps={{ maxLength: 128 }}
                    helperText={`${oocyteForm.embryos_frozen_stage?.length || 0}/128`}
                    sx={{ backgroundColor: '#fafafa', '& .MuiOutlinedInput-root': { height: '44px' } }}
                  />
                </Grid>
              </Grid>

              {/* Actions */}
              <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                {oocyteEditId && (
                  <Button size="small" variant="outlined" onClick={() => { setOocyteEditId(null); setOocyteForm(emptyOocyte()); }}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '7px', borderColor: '#dee2e6', color: '#6c757d' }}>
                    Cancel Edit
                  </Button>
                )}
                <Button size="small" variant="contained" onClick={handleOocyteSave} disabled={savingOocyte}
                  startIcon={savingOocyte ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SaveIcon sx={{ fontSize: 15 }} />}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '7px', backgroundColor: '#e65100 !important', color: 'white !important' }}>
                  {oocyteEditId ? 'Update' : 'Save'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Dialog>

      {/* ── Embryo Transfer Modal ── */}
      {embroTransferModalOpen.open && (
        <EmbroTransferModal
          open={embroTransferModalOpen.open}
          onClose={() => setEmbroTransferModalOpen({ open: false, folliculometryId: null })}
          procedureId={parseInt(procedureId, 10)}
          visitId={procedure?.visit_id || null}
          contactId={patient?.id || null}
          primaryColor={primaryColor}
        />
      )}

      {/* ── FET Monitoring Modal ── */}
      {fetMonitoringModalOpen.open && (
        <EmbryoFetMonitoringModal
          open={fetMonitoringModalOpen.open}
          onClose={() => setFetMonitoringModalOpen({ open: false, procedureId: null })}
          procedureId={parseInt(procedureId, 10)}
          visitId={procedure?.visit_id || null}
          contactId={patient?.id || null}
          primaryColor={primaryColor}
        />
      )}

      {/* ── Embryo Outcome Modal ── */}
      {embryoOutcomeModalOpen.open && (
        <EmbryoOutcomeModal
          open={embryoOutcomeModalOpen.open}
          onClose={() => setEmbryoOutcomeModalOpen({ open: false, procedureId: null })}
          procedureId={parseInt(procedureId, 10)}
          primaryColor={primaryColor}
        />
      )}

      {/* ── Final Outcome Modal ── */}
      {finalOutcomeModalOpen.open && (
        <FinalOutcomeModal
          open={finalOutcomeModalOpen.open}
          onClose={() => setFinalOutcomeModalOpen({ open: false, procedureId: null })}
          procedureId={parseInt(procedureId, 10)}
          primaryColor={primaryColor}
        />
      )}

      {/* ── Print Preview ── */}
      <FolliculometryPrint        open={printDialog.open}
        onClose={() => setPrintDialog({ open: false, folliculometry: null, days: [] })}
        folliculometry={printDialog.folliculometry}
        days={printDialog.days || []}
        patient={patient}
        procedure={procedure}
      />

      {/* ── Comparison Sheet Report (CS Report) ── */}
      <FolliculometryComparisonSheet
        open={comparisonSheet.open}
        onClose={() => setComparisonSheet({ open: false, folliculometry: null, days: [], fullScreen: false })}
        folliculometry={comparisonSheet.folliculometry}
        days={comparisonSheet.days || []}
        patient={patient}
        procedure={procedure}
        fullScreen={comparisonSheet.fullScreen}
      />

      <Footer />
    </DashboardLayout>
  );
}

// ── DayCard ────────────────────────────────────────────────────────────────────
DayCard.propTypes = {
  day: PropTypes.shape({
    id: PropTypes.number,
    date: PropTypes.string,
    cycle_day: PropTypes.string,
    cycle_type: PropTypes.string,
    endometrium: PropTypes.string,
    actions: PropTypes.string,
    comments: PropTypes.string,
    records: PropTypes.array,
    params: PropTypes.array,
    drugs: PropTypes.array,
  }).isRequired,
  folliculometryId: PropTypes.number.isRequired,
  folliculometryStatus: PropTypes.string,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  primaryColor: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  secondaryColor: PropTypes.string.isRequired,
};

function DayCard({ day, folliculometryId, canEdit, canDelete, folliculometryStatus, onEdit, onDelete, primaryColor, textColor, secondaryColor }) {
  const grid = recordsToGrid(day.records || []);
  const hasAnyCount = (day.records || []).some(r => r.count > 0);

  const paramsByType = {};
  (day.params || []).forEach(p => {
    if (!paramsByType[p.param_type]) paramsByType[p.param_type] = [];
    paramsByType[p.param_type].push(p);
  });

  return (
    <Accordion disableGutters elevation={0}
      sx={{ border: '1px solid #e9ecef', borderRadius: '8px !important', mb: 1.5, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}
        sx={{ px: 2, py: 0.5, minHeight: 48, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
        <Box flex={1} display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography sx={{ fontWeight: 700, color: textColor, fontSize: '0.875rem' }}>
            {day.date ? new Date(day.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
          </Typography>
          {day.cycle_day && <Chip label={`${day.cycle_day}`} size="small" sx={{ height: 18, fontSize: '0.68rem', fontWeight: 600, backgroundColor: 'rgba(0,121,142,0.08)', color: primaryColor }} />}
          {day.cycle_type && <Chip label={day.cycle_type} size="small" sx={{ height: 18, fontSize: '0.68rem', fontWeight: 600, backgroundColor: '#f3e5f5', color: '#6a1b9a' }} />}
          {day.endometrium && <Typography sx={{ fontSize: '0.75rem', color: secondaryColor }}>Endo: {day.endometrium}</Typography>}
        </Box>
        <Box display="flex" gap={0.5} mr={1} onClick={e => e.stopPropagation()}>
          {canEdit && folliculometryStatus !== 'COMPLETED' && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={onEdit} sx={{ color: '#f57c00', width: 26, height: 26 }}>
                <EditIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && folliculometryStatus !== 'COMPLETED' && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={onDelete} sx={{ color: '#dc3545', width: 26, height: 26 }}>
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
        {/* Follicle summary */}
        {hasAnyCount && (
          <Box mb={1.5}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: textColor, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Follicle Counts</Typography>
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e9ecef' }}>
              <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', backgroundColor: '#f8f9fa', py: 0.5 }}>Size</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem', backgroundColor: '#fce4ec', color: '#c62828', py: 0.5 }}>Right</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.72rem', backgroundColor: '#e3f2fd', color: '#1565c0', py: 0.5 }}>Left</TableCell>
                  </TableRow>
                  {FOLLICLE_SIZES.filter(s => (grid[s]?.right || 0) + (grid[s]?.left || 0) > 0).map(size => (
                    <TableRow key={size}>
                      <TableCell sx={{ fontSize: '0.8rem', py: 0.4, fontWeight: 600 }}>{size}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', py: 0.4 }}>{grid[size]?.right || 0}</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.8rem', py: 0.4 }}>{grid[size]?.left || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Params */}
        {Object.keys(paramsByType).length > 0 && (
          <Box mb={1.5}>
            {Object.entries(paramsByType).map(([type, ps]) => (
              <Box key={type} mb={0.5}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: textColor, textTransform: 'uppercase', letterSpacing: '0.4px', display: 'inline', mr: 1 }}>{type}:</Typography>
                {ps.map((p, i) => (
                  <Chip key={i} label={`${p.param_name}${p.param_value ? ` (${p.param_value})` : ''}`} size="small"
                    sx={{ height: 18, fontSize: '0.68rem', mr: 0.5, backgroundColor: '#f5f5f5', color: textColor }} />
                ))}
              </Box>
            ))}
          </Box>
        )}

        {/* Drugs */}
        {(day.drugs || []).length > 0 && (
          <Box mb={1.5}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: textColor, textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.5 }}>Drugs</Typography>
            <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e9ecef' }}>
              <Table size="small" sx={{ width: '100%' }}>
                <TableBody>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', py: 0.5 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', py: 0.5 }}>Drug Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', py: 0.5 }}>Dose</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', py: 0.5 }}>Route</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', py: 0.5 }}>Days</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.68rem', py: 0.5 }}>Time</TableCell>
                  </TableRow>
                  {day.drugs.map((d, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontSize: '0.75rem', py: 0.4 }}>{d.drug_type || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 0.4 }}>{d.drug_name || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 0.4 }}>{d.dose || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 0.4 }}>{d.route || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 0.4 }}>{d.days || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 0.4 }}>
                        {(d.times || []).length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                            {d.times.map(t => (
                              <Chip key={t} label={t} size="small"
                                sx={{ height: 18, fontSize: '0.65rem', backgroundColor: '#f3e5f5', color: '#6a1b9a' }} />
                            ))}
                          </Box>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Actions & Comments */}
        {(day.actions || day.comments) && (
          <Grid container spacing={1}>
            {day.actions && (
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: textColor, textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.25 }}>Actions</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: secondaryColor, whiteSpace: 'pre-wrap' }}>{day.actions}</Typography>
              </Grid>
            )}
            {day.comments && (
              <Grid item xs={12} sm={6}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: textColor, textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.25 }}>Comments</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: secondaryColor, whiteSpace: 'pre-wrap' }}>{day.comments}</Typography>
              </Grid>
            )}
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
